import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import {
  FiActivity,
  FiAlertCircle,
  FiArrowLeft,
  FiAward,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCoffee,
  FiSearch,
  FiTrendingUp,
  FiZap,
} from "react-icons/fi";
import Avatar from "./Avatar";
import FichaResumen from "./FichaResumen";
import "./ClientesPanel.css";

/*
 * Panel de clientes del entrenador. Sustituye a la lista de "Usuarios", que
 * sólo enseñaba nombre, correo y tres botones.
 *
 * Aquí se ve de un vistazo quién entrena, quién lleva días sin aparecer y
 * quién no tiene rutina asignada, y al abrir una ficha, todo su historial.
 */

const OBJETIVOS = { bajar: "Bajar peso", subir: "Subir peso" };
const SENSACIONES = { mal: "Flojo", normal: "Normal", bien: "Fuerte" };

const fecha = (d) =>
  d ? new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "—";

const fechaCorta = (d) =>
  d ? new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short" }) : "—";

const duracion = (seg) => {
  if (!seg) return "—";
  const m = Math.round(seg / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};

function desdeHace(dias) {
  if (dias === null || dias === undefined) return "Nunca ha entrenado";
  if (dias === 0) return "Hoy";
  if (dias === 1) return "Ayer";
  if (dias < 7) return `Hace ${dias} días`;
  if (dias < 30) return `Hace ${Math.floor(dias / 7)} semanas`;
  return `Hace ${Math.floor(dias / 30)} meses`;
}

/* ─── Tarjeta de la lista ─── */
function TarjetaCliente({ c, onAbrir }) {
  const nombre = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email;
  return (
    <button type="button" className="cp-tarjeta" onClick={() => onAbrir(c._id)}>
      <div className="cp-tarjeta-top">
        <Avatar userId={c._id} nombre={c.firstName} apellido={c.lastName} tamano={46} />
        <div className="cp-avatar cp-avatar-oculto" aria-hidden="true">
          {(c.firstName?.[0] || c.email?.[0] || "?").toUpperCase()}
        </div>
        <div className="cp-tarjeta-id">
          <strong>{nombre}</strong>
          <span>{c.email}</span>
        </div>
        <div className="cp-tarjeta-estados">
          {!c.approved && <span className="cp-eti cp-eti-aviso">Sin aprobar</span>}
          {c.enRiesgo && c.approved && (
            <span className="cp-eti cp-eti-riesgo">
              <FiAlertCircle /> Sin entrenar
            </span>
          )}
        </div>
      </div>

      <dl className="cp-tarjeta-datos">
        <div>
          <dt>Último entreno</dt>
          <dd>{desdeHace(c.entrenos?.diasDesde)}</dd>
        </div>
        <div>
          <dt>Este mes</dt>
          <dd>{c.entrenos?.mes || 0} entrenos</dd>
        </div>
        <div>
          <dt>Rutina</dt>
          <dd className={c.rutina ? "" : "cp-falta"}>
            {c.rutina ? `${c.rutina.sesiones} sesiones` : "Sin asignar"}
          </dd>
        </div>
        <div>
          <dt>Plan de comidas</dt>
          <dd className={c.plan ? "" : "cp-falta"}>{c.plan ? "Asignado" : "Sin asignar"}</dd>
        </div>
      </dl>
    </button>
  );
}

/* ─── Ficha completa ─── */
function Ficha({ id, onVolver }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [generando, setGenerando] = useState("");
  const [propuesta, setPropuesta] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { token } = await getSession();
        const res = await axios.get(`${API_URL}/clientes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDatos(res.data);
      } catch {
        setError("No se pudo cargar la ficha.");
      }
    })();
  }, [id]);

  const generar = async (que) => {
    setGenerando(que);
    setPropuesta(null);
    try {
      const { token } = await getSession();
      const res = await axios.post(
        `${API_URL}/clientes/${id}/${que === "rutina" ? "generar-rutina" : "generar-plan"}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPropuesta({ que, datos: res.data.propuesta });
    } catch {
      setError("No se pudo generar la propuesta.");
    } finally {
      setGenerando("");
    }
  };

  if (error) return <p className="app-estado-error">{error}</p>;
  if (!datos) return <div className="app-estado">Cargando ficha…</div>;

  const { user, objetivos, rutina, plan, entrenos, resumen, records, comidas, mensajes } = datos;
  const nombre = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

  return (
    <div className="cp-ficha">
      <button type="button" className="cp-volver" onClick={onVolver}>
        <FiArrowLeft /> Todos los clientes
      </button>

      {/* Cabecera */}
      <header className="cp-ficha-top">
        <Avatar userId={user._id} nombre={user.firstName} apellido={user.lastName} tamano={66} />
        <div>
          <h2>{nombre}</h2>
          <p className="cp-ficha-contacto">
            {user.email}
            {user.phone && <> · {user.phone}</>}
          </p>
          <div className="cp-ficha-etis">
            <span className={`cp-eti ${user.approved ? "cp-eti-ok" : "cp-eti-aviso"}`}>
              {user.approved ? "Aprobado" : "Sin aprobar"}
            </span>
            {user.goal && <span className="cp-eti">{OBJETIVOS[user.goal] || user.goal}</span>}
            <span className="cp-eti">Alta: {fecha(user.createdAt)}</span>
          </div>
        </div>
      </header>

      {/* Lo que contó él: de aquí sale la rutina y el plan */}
      <section className="cp-bloque">
        <h3>
          Su ficha
          {datos.perfil && (
            <span className={datos.perfil.completo ? "cp-eti cp-eti-ok" : "cp-eti cp-eti-aviso"}>
              {datos.perfil.completo ? "Completa" : `${datos.perfil.porcentaje}% rellenada`}
            </span>
          )}
        </h3>
        <FichaResumen ficha={datos.perfil} />
      </section>

      {/* Datos físicos y objetivos */}
      <section className="cp-bloque">
        <h3>Datos y objetivos</h3>
        <div className="cp-rejilla">
          <div className="cp-dato">
            <span>Peso</span>
            <strong>{user.weight ? `${user.weight} kg` : "—"}</strong>
          </div>
          <div className="cp-dato">
            <span>Altura</span>
            <strong>{user.height ? `${user.height} cm` : "—"}</strong>
          </div>
          <div className="cp-dato">
            <span>Calorías / día</span>
            <strong>{objetivos?.calorias ? `${objetivos.calorias} kcal` : "—"}</strong>
          </div>
          <div className="cp-dato">
            <span>Proteína</span>
            <strong>{objetivos?.proteinas ? `${objetivos.proteinas} g` : "—"}</strong>
          </div>
          <div className="cp-dato">
            <span>Carbohidratos</span>
            <strong>{objetivos?.carbohidratos ? `${objetivos.carbohidratos} g` : "—"}</strong>
          </div>
          <div className="cp-dato">
            <span>Grasas</span>
            <strong>{objetivos?.grasas ? `${objetivos.grasas} g` : "—"}</strong>
          </div>
        </div>
      </section>

      {/* Actividad */}
      <section className="cp-bloque">
        <h3>Actividad</h3>
        <div className="cp-rejilla cp-rejilla-4">
          <div className="cp-dato cp-dato-fuerte">
            <FiActivity />
            <strong>{resumen.totalEntrenos}</strong>
            <span>entrenos</span>
          </div>
          <div className="cp-dato cp-dato-fuerte">
            <FiTrendingUp />
            <strong>{(resumen.volumenTotal || 0).toLocaleString("es-ES")}</strong>
            <span>kg movidos</span>
          </div>
          <div className="cp-dato cp-dato-fuerte">
            <FiClock />
            <strong>{resumen.minutosTotal}</strong>
            <span>minutos</span>
          </div>
          <div className="cp-dato cp-dato-fuerte">
            <FiCalendar />
            <strong>{fechaCorta(resumen.ultimo)}</strong>
            <span>último</span>
          </div>
        </div>
      </section>

      {/* Rutina y plan */}
      <section className="cp-bloque">
        <h3>Rutina y nutrición</h3>
        <div className="cp-asignaciones">
          <div className="cp-asig">
            <div>
              <strong>Rutina</strong>
              <span>
                {rutina
                  ? `${rutina.sesiones?.length || 0} sesiones · actualizada ${fechaCorta(rutina.updatedAt)}`
                  : "Sin asignar"}
              </span>
            </div>
            <button
              type="button"
              className="cp-btn-gen"
              onClick={() => generar("rutina")}
              disabled={generando === "rutina"}
            >
              <FiZap /> {generando === "rutina" ? "Generando…" : "Generar"}
            </button>
          </div>

          <div className="cp-asig">
            <div>
              <strong>Plan de comidas</strong>
              <span>
                {plan ? `Actualizado ${fechaCorta(plan.updatedAt)}` : "Sin asignar"}
              </span>
            </div>
            <button
              type="button"
              className="cp-btn-gen"
              onClick={() => generar("plan")}
              disabled={generando === "plan"}
            >
              <FiZap /> {generando === "plan" ? "Generando…" : "Generar"}
            </button>
          </div>
        </div>

        {propuesta && (
          <div className="cp-propuesta">
            <p className="cp-propuesta-aviso">
              Propuesta generada con el peso y el objetivo de {user.firstName || "este cliente"}.
              Revísala antes de asignarla: no se ha guardado nada todavía.
            </p>
            {propuesta.que === "rutina" ? (
              <ol className="cp-propuesta-lista">
                {propuesta.datos.map((s, i) => (
                  <li key={i}>
                    <strong>{s.nombre}</strong>
                    <span>
                      {s.ejercicios.map((e) => e.nombre).join(" · ")}
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <ol className="cp-propuesta-lista">
                {(propuesta.datos.dias || propuesta.datos.secciones || [])
                  .slice(0, 7)
                  .map((d, i) => (
                    <li key={i}>
                      <strong>{d.nombre || d.fecha || `Día ${i + 1}`}</strong>
                      <span>
                        {(d.comidas || [])
                          .map((c) => (typeof c === "object" ? c.nombre : c))
                          .join(" · ")}
                      </span>
                    </li>
                  ))}
              </ol>
            )}
            <p className="cp-propuesta-pie">
              Para asignarla, cópiala en{" "}
              {propuesta.que === "rutina" ? "Rutinas" : "Nutrición"} y guárdala desde ahí.
            </p>
          </div>
        )}
      </section>

      {/* Récords */}
      {records?.length > 0 && (
        <section className="cp-bloque">
          <h3>
            <FiAward /> Mejores marcas
          </h3>
          <ul className="cp-records">
            {records.slice(0, 8).map((r) => (
              <li key={r.nombre}>
                <span className="cp-record-nombre">{r.nombre}</span>
                <span className="cp-record-peso">
                  {r.peso} kg × {r.repeticiones || "—"}
                </span>
                <span className="cp-record-fecha">{fechaCorta(r.fecha)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Entrenos */}
      {entrenos?.length > 0 && (
        <section className="cp-bloque">
          <h3>Últimos entrenamientos</h3>
          <ul className="cp-entrenos">
            {entrenos.slice(0, 10).map((l) => (
              <li key={l._id}>
                <div>
                  <strong>{l.sesionNombre || "Entrenamiento"}</strong>
                  <span>{fecha(l.empezadoEn)}</span>
                </div>
                <div className="cp-entreno-cifras">
                  <span>{duracion(l.duracionSeg)}</span>
                  <span>{l.seriesCompletadas} series</span>
                  <span>{(l.volumenKg || 0).toLocaleString("es-ES")} kg</span>
                  {l.sensacion && (
                    <span className={`cp-sens cp-sens-${l.sensacion}`}>
                      {SENSACIONES[l.sensacion]}
                    </span>
                  )}
                </div>
                {l.notas && <p className="cp-entreno-nota">“{l.notas}”</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Comidas */}
      {comidas?.length > 0 && (
        <section className="cp-bloque">
          <h3>
            <FiCoffee /> Últimas comidas registradas
          </h3>
          <ul className="cp-comidas">
            {comidas.slice(0, 10).map((c) => (
              <li key={c._id}>
                <span>{c.name || c.nombre || "Comida"}</span>
                <span className="cp-comida-kcal">{c.calories} kcal</span>
                <span className="cp-comida-fecha">{fechaCorta(c.date)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {mensajes > 0 && (
        <p className="cp-mensajes">{mensajes} mensajes intercambiados en el chat.</p>
      )}
    </div>
  );
}

/* ─── Panel ─── */
function ClientesPanel() {
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [abierto, setAbierto] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { token } = await getSession();
      const res = await axios.get(`${API_URL}/clientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientes(res.data);
      setError("");
    } catch {
      setError("No se pudieron cargar los clientes.");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return clientes.filter((c) => {
      if (q) {
        const texto = `${c.firstName || ""} ${c.lastName || ""} ${c.email || ""}`.toLowerCase();
        if (!texto.includes(q)) return false;
      }
      if (filtro === "riesgo") return c.enRiesgo;
      if (filtro === "sinRutina") return !c.rutina;
      if (filtro === "sinAprobar") return !c.approved;
      if (filtro === "activos") return (c.entrenos?.mes || 0) > 0;
      return true;
    });
  }, [clientes, busca, filtro]);

  if (abierto) return <Ficha id={abierto} onVolver={() => setAbierto(null)} />;

  const enRiesgo = clientes.filter((c) => c.enRiesgo).length;
  const sinRutina = clientes.filter((c) => !c.rutina).length;
  const activos = clientes.filter((c) => (c.entrenos?.mes || 0) > 0).length;
  const sinAprobar = clientes.filter((c) => !c.approved).length;

  return (
    <div className="cp">
      {/* Cifras */}
      <section className="cp-cifras">
        <div>
          <strong>{clientes.length}</strong>
          <span>clientes</span>
        </div>
        <div className={activos > 0 ? "cp-cifra-ok" : ""}>
          <FiCheckCircle />
          <strong>{activos}</strong>
          <span>entrenando este mes</span>
        </div>
        <div className={enRiesgo > 0 ? "cp-cifra-riesgo" : ""}>
          <FiAlertCircle />
          <strong>{enRiesgo}</strong>
          <span>sin entrenar</span>
        </div>
        <div className={sinRutina > 0 ? "cp-cifra-aviso" : ""}>
          <strong>{sinRutina}</strong>
          <span>sin rutina</span>
        </div>
      </section>

      {/* Buscador y filtros */}
      <div className="cp-controles">
        <label className="cp-busca">
          <FiSearch />
          <input
            type="search"
            placeholder="Buscar por nombre o correo"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </label>
        <div className="cp-filtros">
          {[
            ["todos", `Todos (${clientes.length})`],
            ["activos", `Activos (${activos})`],
            ["riesgo", `Sin entrenar (${enRiesgo})`],
            ["sinRutina", `Sin rutina (${sinRutina})`],
            ["sinAprobar", `Sin aprobar (${sinAprobar})`],
          ].map(([v, t]) => (
            <button
              key={v}
              type="button"
              className={filtro === v ? "cp-filtro-on" : ""}
              onClick={() => setFiltro(v)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {cargando && <div className="app-estado">Cargando clientes…</div>}
      {error && <p className="app-estado-error">{error}</p>}

      {!cargando && visibles.length === 0 && (
        <p className="cp-sin">No hay clientes que encajen con ese filtro.</p>
      )}

      <div className="cp-lista">
        {visibles.map((c) => (
          <TarjetaCliente key={c._id} c={c} onAbrir={setAbierto} />
        ))}
      </div>
    </div>
  );
}

export default ClientesPanel;
